import {
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  MutableRefObject
} from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { getBoundingBoxStructured, getRotation } from './common'
import { Tooltip } from 'antd'
import { TranslationTextForTooltip } from 'admin/lib/Translations/TranslationTextForTooltip'

type OcrRegionsProps = {
  type: 'pdf' | 'image'
  ocrData: any
  width: number
  height: number
  rotation: number
  isRotated: boolean
  ocrEnabled: boolean
  handleExtractionValueClicked: (event: any) => void
  pageNumber: number
  attachmentIndex: number
  ocrSearchText: string
  fit?: string
  projectLocale: string | null
}

export const OcrRegions = ({
  type,
  ocrData,
  width: parentWidth,
  height: parentHeight,
  rotation,
  isRotated,
  ocrEnabled,
  handleExtractionValueClicked,
  pageNumber,
  attachmentIndex,
  ocrSearchText = '',
  projectLocale
}: OcrRegionsProps) => {
  const containerRef = useRef<HTMLDivElement>()

  const pageOcrData = useMemo(() => {
    const NO_PAGE_NUMBER = 0
    return {
      height:
        Array.isArray(ocrData.height) && ocrData.height[pageNumber - 1]
          ? ocrData.height[pageNumber - 1]
          : ocrData.height,
      width:
        Array.isArray(ocrData.width) && ocrData.width[pageNumber - 1]
          ? ocrData.width[pageNumber - 1]
          : ocrData.width,
      subRegions: ocrData.subRegions.filter(
        ({ pageNumber: regionPageNumber }) =>
          // eslint-disable-next-line eqeqeq
          regionPageNumber == pageNumber || regionPageNumber == NO_PAGE_NUMBER
      )
    }
  }, [ocrData, pageNumber])

  return (
    <Container
      ref={containerRef as MutableRefObject<HTMLDivElement>}
      isPDF={type === 'pdf'}
      coordinates={{
        width: parentWidth,
        height: parentHeight
      }}
      rotation={rotation}
      isRotated={isRotated}
    >
      {ocrEnabled && (
        <OCRBoxes
          ocrData={pageOcrData}
          containerWidth={parentWidth}
          containerHeight={parentHeight}
          pageNumber={pageNumber}
          attachmentIndex={attachmentIndex}
          handleExtractionValueClicked={handleExtractionValueClicked}
          ocrSearchText={ocrSearchText}
          projectLocale={projectLocale}
        />
      )}
    </Container>
  )
}

interface RouteParams extends Record<string, string | undefined> {
  recordId: string | undefined
}

function OCRBoxes({
  ocrData,
  containerWidth,
  containerHeight,
  pageNumber,
  attachmentIndex,
  handleExtractionValueClicked,
  ocrSearchText,
  projectLocale
}) {
  const [markedBoxes, setMarkedBoxes] = useState({})
  const lastFocus = useRef<HTMLInputElement>() as MutableRefObject<
    HTMLInputElement | undefined | null
  >
  const { recordId } = useParams<RouteParams>()
  const handleStart = useCallback(e => {
    lastFocus.current = document.activeElement as HTMLInputElement
  }, [])

  const handleEnd = useCallback(() => {
    if (lastFocus.current === document.activeElement) {
      lastFocus.current?.blur?.()
    }
    if (lastFocus.current) {
      lastFocus.current?.focus?.()
      lastFocus.current = undefined
    }
  }, [])

  const adjustXCoOrdinate = x => {
    return (x * containerWidth) / ocrData.width
  }

  const adjustYCoOrdinate = y => {
    return (y * containerHeight) / ocrData.height
  }

  const handleRightClick = (event, index: number): void => {
    setMarkedBoxes(prevState => {
      sessionStorage.setItem(
        `${recordId}-${attachmentIndex}-${pageNumber}-marked`,
        JSON.stringify({
          ...prevState,
          [index]: !prevState[index]
        })
      )
      return { ...prevState, [index]: !prevState[index] }
    })

    event.preventDefault()
    event.stopPropagation()
    handleEnd()
  }

  useEffect(() => {
    const storedMarked = sessionStorage.getItem(
      `${recordId}-${attachmentIndex}-${pageNumber}-marked`
    )
    if (storedMarked) {
      setMarkedBoxes(JSON.parse(storedMarked))
    }
  }, [attachmentIndex, pageNumber, recordId])

  const cleanText = (ocrText: string): string => {
    let cleanedText = ocrText
      .replace('<!-- FigureContent="', '')
      .replace('<!-- PageNumber="', '')
      .replace('<!-- PageHeader="', '')
      .replace('<!-- PageFooter="', '')
      .replace('" -->', '')
      .replace('===', '')
      .replace(/\\\*/g, '*')
      .replace(/\\_/g, '_')
      .replace('×', 'x')
    if (cleanedText.startsWith(': ')) {
      cleanedText = cleanedText.replace(': ', '')
    }
    return cleanedText
  }

  return (
    <svg viewBox={`0 0 ${containerWidth} ${containerHeight}`}>
      {ocrData.subRegions.map((subRegion, index) => {
        const boundingBox = getBoundingBoxStructured(subRegion.bounds.bounds)
        const cleanedText = cleanText(subRegion.text)
        return (
          <Tooltip
            title={
              <TranslationTextForTooltip
                cleanedText={cleanedText}
                projectLocale={projectLocale}
              />
            }
            key={index}
            placement={'right'}
          >
            <OCRBox
              data-testid="ocr-polygon"
              key={index}
              $searchMatch={
                ocrSearchText &&
                cleanedText.toLowerCase().includes(ocrSearchText.toLowerCase())
              }
              $marked={markedBoxes[index]}
              points={[
                adjustXCoOrdinate(boundingBox.topLeftX),
                adjustYCoOrdinate(boundingBox.topLeftY),
                adjustXCoOrdinate(boundingBox.topRightX),
                adjustYCoOrdinate(boundingBox.topRightY),
                adjustXCoOrdinate(boundingBox.bottomRightX),
                adjustYCoOrdinate(boundingBox.bottomRightY),
                adjustXCoOrdinate(boundingBox.bottomLeftX),
                adjustYCoOrdinate(boundingBox.bottomLeftY)
              ].join(',')}
              onMouseDown={handleStart}
              onClick={() => {
                // Store selected text in session storage so it can be read by line item field (if needed)

                sessionStorage.setItem('clipboard', cleanedText)

                handleEnd()
                handleExtractionValueClicked(cleanedText)

                // Clear session storage
                sessionStorage.removeItem('clipboard')
              }}
              onContextMenu={event => handleRightClick(event, index)}
              aria-label={cleanedText}
            />
          </Tooltip>
        )
      })}
    </svg>
  )
}

const OCRBox = styled.polygon<{ $searchMatch?: boolean; $marked?: boolean }>`
  cursor: pointer;
  stroke: ${props =>
    props.$searchMatch
      ? 'rgb(121, 66, 230)'
      : props.$marked
        ? 'green'
        : 'grey'};
  stroke-width: ${props => (props.$searchMatch ? '2px' : '1px')};
  fill: ${props =>
    props.$marked
      ? 'rgba(0, 150, 0, 0.15)'
      : props.$searchMatch
        ? 'rgba(121, 66, 230, 0.1)'
        : 'rgba(150, 150, 150, 0.15)'};
`

const Container = styled.div<{
  isPDF: boolean
  coordinates: { width: number; height: number }
  rotation: number
  isRotated: boolean
}>`
  position: absolute;
  top: ${props => (props.isPDF || props.isRotated ? '50%' : '0')};
  left: ${props => (props.isPDF || props.isRotated ? '50%' : '0')};
  width: ${props => props.coordinates.width}px;
  height: ${props => props.coordinates.height}px;
  aspect-ratio: initial;
  max-height: none;
  transform: translate(
      ${props => (props.isPDF || props.isRotated ? '-50%, -50%' : '0,0')}
    )
    rotate(${props => getRotation(props.rotation)}deg);
`
